// Job ids -> names for a PRE-RENEWAL server (rathena src/config/renewal.hpp
// defines PRERE). Taken from client/Assets/Scripts/RagnaNet/JobNames.cs, which
// is generated from rathena's e_job; renewal-only jobs (3rd/4th classes,
// Kagerou, Rebellion...) are left out because this server cannot have them.
// Mounted ids (13, 21, 4014, 4022, 4036, 4044, 4048) share their base name.

export const JOB_NAMES: Record<number, string> = {
  0: 'Novice', 1: 'Swordman', 2: 'Mage', 3: 'Archer', 4: 'Acolyte', 5: 'Merchant', 6: 'Thief',
  7: 'Knight', 8: 'Priest', 9: 'Wizard', 10: 'Blacksmith', 11: 'Hunter', 12: 'Assassin',
  13: 'Knight', 14: 'Crusader', 15: 'Monk', 16: 'Sage', 17: 'Rogue', 18: 'Alchemist',
  19: 'Bard', 20: 'Dancer', 21: 'Crusader', 22: 'Wedding', 23: 'Super Novice',
  24: 'Gunslinger', 25: 'Ninja', 26: 'Christmas', 27: 'Summer', 28: 'Hanbok',
  4001: 'High Novice', 4002: 'High Swordman', 4003: 'High Mage', 4004: 'High Archer',
  4005: 'High Acolyte', 4006: 'High Merchant', 4007: 'High Thief', 4008: 'Lord Knight',
  4009: 'High Priest', 4010: 'High Wizard', 4011: 'Whitesmith', 4012: 'Sniper',
  4013: 'Assassin Cross', 4014: 'Lord Knight', 4015: 'Paladin', 4016: 'Champion',
  4017: 'Professor', 4018: 'Stalker', 4019: 'Creator', 4020: 'Clown', 4021: 'Gypsy',
  4022: 'Paladin', 4023: 'Baby', 4024: 'Baby Swordman', 4025: 'Baby Mage',
  4026: 'Baby Archer', 4027: 'Baby Acolyte', 4028: 'Baby Merchant', 4029: 'Baby Thief',
  4030: 'Baby Knight', 4031: 'Baby Priest', 4032: 'Baby Wizard', 4033: 'Baby Blacksmith',
  4034: 'Baby Hunter', 4035: 'Baby Assassin', 4036: 'Baby Knight', 4037: 'Baby Crusader',
  4038: 'Baby Monk', 4039: 'Baby Sage', 4040: 'Baby Rogue', 4041: 'Baby Alchemist',
  4042: 'Baby Bard', 4043: 'Baby Dancer', 4044: 'Baby Crusader', 4045: 'Super Baby',
  4046: 'Taekwon', 4047: 'Star Gladiator', 4048: 'Star Gladiator', 4049: 'Soul Linker',
};

export function jobName(id: number): string {
  return JOB_NAMES[id] ?? `Job ${id}`;
}

/** Transcendent (rebirth) classes, High Novice .. Paladin: each one reached base 99 before rebirth. */
export function isTranscendent(id: number): boolean {
  return id >= 4001 && id <= 4022;
}

/**
 * The job level cap of a class on this server (db/pre-re/job_exp.yml MaxJobLevel):
 * Novice / High Novice / Baby 10, first classes and Taekwon 50, second classes
 * and Soul Linker 50, transcendent second classes 70, Gunslinger and Ninja 70,
 * Star Gladiator 50, Super Novice 99. Base level is 99 for every class.
 */
export const MAX_BASE_LEVEL = 99;
export function maxJobLevel(id: number): number {
  if (id === 23 || id === 4045) return 99; // Super Novice, Super Baby
  if (id === 0 || id === 4001 || id === 4023) return 10; // Novice, High Novice, Baby
  if (id === 24 || id === 25) return 70; // Gunslinger, Ninja
  if (id >= 4008 && id <= 4022) return 70; // transcendent second classes
  return 50;
}

/**
 * The skill points a character of this class and job level has earned in all
 * on its way there, as a real player would: 9 as a Novice (job 10), 49 in a
 * first class (job 50), then job level - 1 in the current class. A rebirth
 * resets the first life's skills, so a transcendent class counts High
 * Novice 9 + High first 49 + its own levels (a 99/70 Lord Knight: 127).
 * Super Novice, Gunslinger, Ninja and Taekwon come straight from Novice.
 */
export function skillPointsFor(id: number, jobLevel: number): number {
  const own = Math.max(0, jobLevel - 1);
  if (id === 0 || id === 4001 || id === 4023) return own; // Novice, High Novice, Baby
  if ((id >= 1 && id <= 6) || (id >= 4002 && id <= 4007) || (id >= 4024 && id <= 4029)) return 9 + own; // first classes
  if (id === 23 || id === 4045 || id === 24 || id === 25 || id === 4046) return 9 + own; // Super Novice, Gunslinger, Ninja, Taekwon
  return 9 + 49 + own; // second and transcendent classes, Star Gladiator, Soul Linker
}

/** Ranking filter groups: every job id that belongs to a first-class line. */
export const JOB_LINES = {
  swordman: { label: 'Swordman', ids: [1, 7, 13, 14, 21, 4002, 4008, 4014, 4015, 4022, 4024, 4030, 4036, 4037, 4044] },
  mage: { label: 'Mage', ids: [2, 9, 16, 4003, 4010, 4017, 4025, 4032, 4039] },
  archer: { label: 'Archer', ids: [3, 11, 19, 20, 4004, 4012, 4020, 4021, 4026, 4034, 4042, 4043] },
  acolyte: { label: 'Acolyte', ids: [4, 8, 15, 4005, 4009, 4016, 4027, 4031, 4038] },
  merchant: { label: 'Merchant', ids: [5, 10, 18, 4006, 4011, 4019, 4028, 4033, 4041] },
  thief: { label: 'Thief', ids: [6, 12, 17, 4007, 4013, 4018, 4029, 4035, 4040] },
  novice: { label: 'Novice', ids: [0, 23, 4001, 4023, 4045] },
  taekwon: { label: 'Taekwon', ids: [4046, 4047, 4048, 4049] },
  gunslinger: { label: 'Gunslinger', ids: [24] },
  ninja: { label: 'Ninja', ids: [25] },
} as const;

export type JobLine = keyof typeof JOB_LINES;

/** rAthena's three fame ladders (char.fame), by the classes that earn it. */
export const FAME_LADDERS = {
  blacksmith: { label: 'Blacksmith', ids: [10, 4011, 4033] },
  alchemist: { label: 'Alchemist', ids: [18, 4019, 4041] },
  taekwon: { label: 'Taekwon', ids: [4046] },
} as const;

export type FameLadder = keyof typeof FAME_LADDERS;
